/**
 * Markdown serializer for the `image` node, replacing prosemirror-markdown's
 * default (which tiptap-markdown falls back to).
 *
 * The default reads `node.attrs.src.replace(...)` unguarded, but an
 * attachment-backed image legitimately has no `src`: wiki images never
 * store one, and a copied image has its `src` cleared so the paste
 * re-links by `attachmentId`. The throw happens while the clipboard's
 * `text/plain` half is being built on copy, which aborts the copy entirely
 * — the clipboard silently keeps whatever it held before. With no URL to
 * point at, the image writes its alt text (an empty string when it has
 * none); the `text/html` half still carries the node with its
 * `data-attachment-id`, which both image extensions parse back into a node
 * on paste (their extra src-less parse rule), re-linking the file by id.
 */

interface MarkdownImageState {
  write(text: string): void
  esc(text: string): string
}

interface MarkdownImageNode {
  attrs: Record<string, unknown>
}

const nonEmptyString = (value: unknown): string | null =>
  typeof value === 'string' && value.length > 0 ? value : null

export function serializeImageMarkdown(state: MarkdownImageState, node: MarkdownImageNode): void {
  const alt = nonEmptyString(node.attrs.alt) ?? ''
  const src = nonEmptyString(node.attrs.src)
  if (!src) {
    if (alt) state.write(state.esc(alt))
    return
  }
  const title = nonEmptyString(node.attrs.title)
  state.write(
    `![${state.esc(alt)}](${src.replace(/[()]/g, '\\$&')}${
      title ? ` "${title.replace(/"/g, '\\"')}"` : ''
    })`,
  )
}

/** The `addStorage()` payload both image extensions share. */
export function imageMarkdownStorage() {
  return { markdown: { serialize: serializeImageMarkdown } }
}
